import { useSupportingDocumentsSubmit } from "@/api/rfs";
import {
  SupportingDocumentData,
  useSupportingDocumentByRfsId,
} from "@/api/supporting-document";
import { REQUIRED_MESSAGE } from "@/utils/constant";
import { getResponseMessage } from "@/utils/helper/getErrorMessage";
import { swalError, swalLoading, swalSuccess } from "@/utils/lib/swal";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { convertLabelStatusToValue } from "./convertLabelStatusToValue";
import {
  CODE_META,
  SupportingDocumentField,
} from "./useCheckingProfileValidations";
import { handleValidationFields } from "./validation";
import { UseFormReturn } from "react-hook-form";

const mediaFieldSchema = z
  .object({
    id: z.string().optional(),
    path: z.string().optional(),
    originalFileName: z.string().optional(),
    name: z.string().optional(),
  })
  .optional();

// Satu field media per code (D00..D07), dinamis dari CODE_META.
const documentFieldsShape = Object.fromEntries(
  Object.keys(CODE_META).map((code) => [code, mediaFieldSchema]),
) as Record<keyof typeof CODE_META, typeof mediaFieldSchema>;

const submitSupportingDocumentsFormSchema = z.object({
  ...documentFieldsShape,
  statusFeederAndOlt: z.string().min(1, { message: REQUIRED_MESSAGE }),
});
export type SubmitSupportingDocumentsFormSchema = z.infer<
  typeof submitSupportingDocumentsFormSchema
>;

// Body submit: supportingDocument.documents[] — SEMUA code (D00..D07),
// selalu dikirim walau user tidak upload apa-apa di sesi ini. Sumber isian:
// upload baru (values) lalu fallback ke documentMappings dari GET
// supporting-document. type wajib ada per object; id/name/originalFileName/
// path boleh kosong.
export function buildSupportingDocumentPayload(
  values: SubmitSupportingDocumentsFormSchema,
  supportingDocument?: SupportingDocumentData,
) {
  return {
    documents: Object.keys(CODE_META).map((code) => {
      const media = values[code as keyof typeof CODE_META];
      if (media?.id) {
        return { ...media, type: code };
      }

      const mapping = supportingDocument?.documentMappings?.[code];
      return {
        path: mapping?.fieldPath,
        originalFileName: mapping?.fieldName,
        type: code,
      };
    }),
    statusFeederAndOlt: values.statusFeederAndOlt,
  };
}

function useDocumentPrepare({
  id,
  fields,
  form,
}: {
  id: string;
  fields: SupportingDocumentField[];
  form: UseFormReturn<SubmitSupportingDocumentsFormSchema>;
}) {
  const navigate = useNavigate();
  const { mutate: submit } = useSupportingDocumentsSubmit();
  const { data: supportingDocument } = useSupportingDocumentByRfsId({
    rfsId: id,
  });

  function handlePrepare(values: SubmitSupportingDocumentsFormSchema) {
    // Validasi field yang diperlukan terlebih dahulu
    const isValidationPassed = handleValidationFields(
      values,
      fields,
      form,
      supportingDocument,
    );

    // Jika validasi gagal, hentikan proses submit
    if (!isValidationPassed) {
      return;
    }

    if (values.statusFeederAndOlt === "OLS - Feeder Done") {
      values.statusFeederAndOlt = "OLS_DONE_FEEDER";
    } else {
      values.statusFeederAndOlt = convertLabelStatusToValue(
        values.statusFeederAndOlt,
      );
    }

    swalLoading.fire();
    submit(
      {
        id,
        data: {
          supportingDocument: buildSupportingDocumentPayload(
            values,
            supportingDocument,
          ),
        },
        params: {
          decision: "no",
          withVariable: "true",
        },
      },
      {
        onSuccess: () => {
          swalSuccess
            .fire({
              text: "RFS Submission process is successful.",
            })
            .then(() => {
              navigate(0);
            });
        },
        onError: (error) => {
          swalError
            .fire({
              text: getResponseMessage(
                error,
                "RFS Submission process is failed.",
              ),
            })
            .then(() => {
              navigate(0);
            });
        },
      },
    );
  }

  return {
    handlePrepare,
  };
}

export { useDocumentPrepare, submitSupportingDocumentsFormSchema };
